import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Loader2, AlertCircle, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui';
import {
	usePatchCompany,
	useUpdateCompanyStatus,
} from '@/services/mutations/company';
import { useUpdateCompanyPassword } from '@/services/mutations/password';
import {
	PASSWORD_BACKEND_INVALID_MESSAGE,
	PASSWORD_BACKEND_MAX_LENGTH,
	PASSWORD_BACKEND_MIN_LENGTH,
	validatePasswordBackend,
} from '@/utils/validations';

interface EditCompanyModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	company: Companies.CompanyDetail | Companies.Company | null;
}

type ModalStep = 'edit' | 'password';

const getCompanyDisplayName = (
	company: Companies.CompanyDetail | Companies.Company,
) => company.trade_name?.trim() || company.legal_name?.trim() || 'Empresa';

const formatCNPJDisplay = (value: string) => {
	const digits = value.replace(/\D/g, '');
	if (digits.length < 14) return value;
	return digits
		.replace(/^(\d{2})(\d)/, '$1.$2')
		.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
		.replace(/\.(\d{3})(\d)/, '.$1/$2')
		.replace(/(\d{4})(\d)/, '$1-$2')
		.slice(0, 18);
};

const formatCNPJInput = (value: string) => {
	return value
		.replace(/\D/g, '')
		.replace(/^(\d{2})(\d)/, '$1.$2')
		.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
		.replace(/\.(\d{3})(\d)/, '.$1/$2')
		.replace(/(\d{4})(\d)/, '$1-$2')
		.slice(0, 18);
};

const SlidingToggle = ({
	label,
	value,
	onChange,
	options,
}: {
	label: string;
	value: boolean;
	onChange: (val: boolean) => void;
	options: { label: string; value: boolean }[];
}) => {
	const activeIndex = options.findIndex((o) => o.value === value);
	const count = options.length;
	const labelClass =
		'ml-1 text-[10px] font-black uppercase tracking-widest text-stone-400 block mb-2';

	return (
		<div className="w-full">
			<label className={labelClass}>{label}</label>
			<div className="relative flex w-full rounded-lg bg-stone-100 p-1">
				<div
					className="absolute bottom-1 top-1 rounded-md bg-white shadow-sm transition-all duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]"
					style={{
						width: `calc((100% - 8px) / ${count})`,
						left: `calc(4px + ${activeIndex} * ((100% - 8px) / ${count}))`,
					}}
				/>
				{options.map((opt) => (
					<button
						key={String(opt.value)}
						type="button"
						onClick={() => onChange(opt.value)}
						className={cn(
							'z-10 flex-1 cursor-pointer rounded-md border-none bg-transparent px-0 py-1.5 text-center text-[10px] font-black uppercase tracking-tight outline-none transition-colors duration-200 focus:outline-none',
							value === opt.value
								? 'text-stone-900'
								: 'text-stone-400 hover:text-stone-600',
						)}
					>
						{opt.label}
					</button>
				))}
			</div>
		</div>
	);
};

export const EditCompanyModal = ({
	isOpen,
	onClose,
	onSuccess,
	company,
}: EditCompanyModalProps) => {
	const [formData, setFormData] = useState({
		legal_name: '',
		trade_name: '',
		cnpj: '',
		billing_email: '',
		active: true,
		banner_img_url: '',
		logo_img_url: '',
	});
	const [step, setStep] = useState<ModalStep>('edit');
	const [passwordData, setPasswordData] = useState({
		password: '',
		confirm_password: '',
	});
	const [errors, setErrors] = useState<Record<string, string>>({});
	const initialFormRef = useRef<{
		legal_name: string;
		trade_name: string;
		cnpj: string;
		billing_email: string;
		active: boolean;
		banner_img_url: string;
		logo_img_url: string;
	} | null>(null);

	const { mutateAsync: patchCompany, isPending: isPatching } =
		usePatchCompany();
	const { mutateAsync: updateStatus, isPending: isStatusPending } =
		useUpdateCompanyStatus();

	const handleClose = () => {
		setStep('edit');
		setPasswordData({ password: '', confirm_password: '' });
		setErrors({});
		onClose();
	};

	const { mutateAsync: updateCompanyPassword, isPending: isSavingPassword } =
		useUpdateCompanyPassword(() => {
			onSuccess?.();
			handleClose();
		});

	const isPasswordStep = step === 'password';

	useEffect(() => {
		if (isOpen && company) {
			setStep('edit');
			setPasswordData({ password: '', confirm_password: '' });
			setErrors({});
			const snap = {
				legal_name: company.legal_name ?? '',
				trade_name: company.trade_name ?? '',
				cnpj: company.cnpj ? formatCNPJDisplay(company.cnpj) : '',
				billing_email: company.billing_email ?? '',
				active: company.active ?? true,
				banner_img_url: company.banner_img_url ?? '',
				logo_img_url: company.logo_img_url ?? '',
			};
			setFormData(snap);
			initialFormRef.current = { ...snap };
		}
	}, [isOpen, company]);

	const validate = () => {
		const newErrors: Record<string, string> = {};
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

		if (!formData.trade_name.trim()) newErrors.trade_name = 'Obrigatório';

		if (
			formData.billing_email.trim() &&
			!emailRegex.test(formData.billing_email)
		) {
			newErrors.billing_email = 'E-mail inválido';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSave = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!company || !validate()) return;

		const cleanCNPJ = formData.cnpj.replace(/\D/g, '');
		const init = initialFormRef.current;
		const initCleanCnpj = init?.cnpj.replace(/\D/g, '') ?? '';

		const payload: Companies.Update = {};

		if (formData.legal_name.trim() !== (init?.legal_name.trim() ?? '')) {
			if (formData.legal_name.trim()) {
				payload.legal_name = formData.legal_name.trim();
			}
		}

		if (formData.trade_name.trim() !== (init?.trade_name.trim() ?? '')) {
			if (formData.trade_name.trim()) {
				payload.trade_name = formData.trade_name.trim();
			}
		}

		if (cleanCNPJ !== initCleanCnpj && cleanCNPJ.length === 14) {
			payload.cnpj = cleanCNPJ;
		}

		if (
			formData.billing_email.trim() !== (init?.billing_email.trim() ?? '')
		) {
			if (formData.billing_email.trim()) {
				payload.billing_email = formData.billing_email.trim();
			}
		}

		if (
			formData.banner_img_url.trim() !==
			(init?.banner_img_url.trim() ?? '')
		) {
			if (formData.banner_img_url.trim()) {
				payload.banner_img_url = formData.banner_img_url.trim();
			}
		}

		if (
			formData.logo_img_url.trim() !== (init?.logo_img_url.trim() ?? '')
		) {
			if (formData.logo_img_url.trim()) {
				payload.logo_img_url = formData.logo_img_url.trim();
			}
		}

		if (formData.active !== company.active) {
			await updateStatus({ id: company.id, active: formData.active });
		}
		if (Object.keys(payload).length > 0) {
			await patchCompany({ id: company.id, body: payload });
		}
		onSuccess?.();
		handleClose();
	};

	const validatePasswordStep = () => {
		const newErrors: Record<string, string> = {};

		if (!passwordData.password.trim()) {
			newErrors.password = 'Obrigatório';
		} else if (passwordData.password.length < PASSWORD_BACKEND_MIN_LENGTH) {
			newErrors.password = 'A senha deve ter no mínimo 6 caracteres';
		} else if (passwordData.password.length > PASSWORD_BACKEND_MAX_LENGTH) {
			newErrors.password = 'A senha deve ter no máximo 255 caracteres';
		} else if (!validatePasswordBackend(passwordData.password)) {
			newErrors.password = PASSWORD_BACKEND_INVALID_MESSAGE;
		}

		if (!passwordData.confirm_password.trim()) {
			newErrors.confirm_password = 'Obrigatório';
		} else if (passwordData.password !== passwordData.confirm_password) {
			newErrors.confirm_password = 'As senhas não coincidem';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSavePassword = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!company || !validatePasswordStep()) return;

		await updateCompanyPassword({
			companyId: company.id,
			newPassword: passwordData.password,
		});
	};

	const goToPasswordStep = () => {
		setPasswordData({ password: '', confirm_password: '' });
		setErrors({});
		setStep('password');
	};

	const goBackToEditStep = () => {
		setPasswordData({ password: '', confirm_password: '' });
		setErrors({});
		setStep('edit');
	};

	if (!isOpen) return null;

	const isPending = isPatching || isStatusPending || isSavingPassword;
	const labelClass =
		'ml-1 text-[10px] font-black uppercase tracking-widest text-stone-300';
	const inputBaseClass =
		'w-full bg-stone-50 border rounded-xl px-4 py-3 text-sm font-bold text-stone-600 outline-none transition-all box-border disabled:opacity-50';

	const getFieldClass = (fieldName: string) =>
		cn(
			inputBaseClass,
			errors[fieldName]
				? 'border-rose-500 bg-rose-50/30 focus:border-rose-600'
				: 'border-stone-200 focus:border-stone-400 focus:bg-white',
		);

	const ErrorMsg = ({ field }: { field: string }) =>
		errors[field] ? (
			<span className="animate-in fade-in slide-in-from-top-1 ml-1 mt-1 flex items-center gap-1 text-[10px] font-bold text-rose-500">
				<AlertCircle size={10} /> {errors[field]}
			</span>
		) : null;

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={isPending ? undefined : handleClose}
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<form
					onSubmit={isPasswordStep ? handleSavePassword : handleSave}
					noValidate
					className="flex flex-col overflow-hidden"
				>
					<div className="flex shrink-0 items-center justify-between border-b border-stone-50 px-8 py-6">
						<div>
							<h2 className="text-lg font-black text-stone-900">
								{isPasswordStep
									? 'Senha de acesso'
									: 'Editar empresa'}
							</h2>
							{isPasswordStep && company ? (
								<p className="mt-1 text-xs font-medium text-stone-400">
									{getCompanyDisplayName(company)} — login no
									portal com CNPJ e esta senha
								</p>
							) : null}
						</div>
						<button
							type="button"
							onClick={handleClose}
							disabled={isPending}
							className="text-stone-400 transition-colors hover:text-stone-600 disabled:opacity-50"
						>
							<X size={20} />
						</button>
					</div>

					<div className="flex-1 space-y-6 overflow-y-auto p-8">
						{isPasswordStep ? (
							<>
								<div>
									<label className={labelClass}>
										SENHA DE ACESSO
									</label>
									<input
										type="password"
										autoComplete="new-password"
										value={passwordData.password}
										onChange={(e) =>
											setPasswordData({
												...passwordData,
												password: e.target.value,
											})
										}
										className={getFieldClass('password')}
										placeholder="Mín. 6 caracteres, com letra e número"
									/>
									<ErrorMsg field="password" />
								</div>
								<div>
									<label className={labelClass}>
										CONFIRMAR SENHA
									</label>
									<input
										type="password"
										autoComplete="new-password"
										value={passwordData.confirm_password}
										onChange={(e) =>
											setPasswordData({
												...passwordData,
												confirm_password:
													e.target.value,
											})
										}
										className={getFieldClass(
											'confirm_password',
										)}
										placeholder="Repita a senha"
									/>
									<ErrorMsg field="confirm_password" />
								</div>
							</>
						) : (
							<>
								<div>
									<label className={labelClass}>
										NOME FANTASIA
									</label>
									<input
										type="text"
										value={formData.trade_name}
										onChange={(e) =>
											setFormData({
												...formData,
												trade_name: e.target.value,
											})
										}
										className={getFieldClass('trade_name')}
										placeholder="Ex: Exemplo Corp"
									/>
									<ErrorMsg field="trade_name" />
								</div>

								<div>
									<label className={labelClass}>
										RAZÃO SOCIAL (opcional)
									</label>
									<input
										type="text"
										value={formData.legal_name}
										onChange={(e) =>
											setFormData({
												...formData,
												legal_name: e.target.value,
											})
										}
										className={getFieldClass('legal_name')}
										placeholder="Ex: Empresa Exemplo Ltda."
									/>
									<ErrorMsg field="legal_name" />
								</div>

								<div>
									<label className={labelClass}>
										CNPJ (opcional)
									</label>
									<input
										type="text"
										value={formData.cnpj}
										onChange={(e) =>
											setFormData({
												...formData,
												cnpj: formatCNPJInput(
													e.target.value,
												),
											})
										}
										className={getFieldClass('cnpj')}
										placeholder="00.000.000/0000-00"
									/>
									<ErrorMsg field="cnpj" />
								</div>

								<div>
									<label className={labelClass}>
										E-MAIL DE FATURAMENTO (opcional)
									</label>
									<input
										type="email"
										value={formData.billing_email}
										onChange={(e) =>
											setFormData({
												...formData,
												billing_email: e.target.value,
											})
										}
										className={getFieldClass(
											'billing_email',
										)}
										placeholder="financeiro@empresa.com"
									/>
									<ErrorMsg field="billing_email" />
								</div>

								<div>
									<label className={labelClass}>
										URL DA IMAGEM DO LOGO
									</label>
									<input
										type="text"
										value={formData.logo_img_url}
										onChange={(e) =>
											setFormData({
												...formData,
												logo_img_url: e.target.value,
											})
										}
										className={getFieldClass(
											'logo_img_url',
										)}
										placeholder="Ex: Empresa Exemplo LTDA"
									/>
									<ErrorMsg field="logo_img_url" />
								</div>

								<div>
									<label className={labelClass}>
										URL DA IMAGEM DO BANNER
									</label>
									<input
										type="text"
										value={formData.banner_img_url}
										onChange={(e) =>
											setFormData({
												...formData,
												banner_img_url: e.target.value,
											})
										}
										className={getFieldClass(
											'banner_img_url',
										)}
										placeholder="Ex: Empresa Exemplo LTDA"
									/>
									<ErrorMsg field="banner_img_url" />
								</div>

								<SlidingToggle
									label="Empresa ativa?"
									value={formData.active}
									onChange={(val) =>
										setFormData({
											...formData,
											active: val,
										})
									}
									options={[
										{ label: 'Sim', value: true },
										{ label: 'Não', value: false },
									]}
								/>

								<button
									type="button"
									onClick={goToPasswordStep}
									disabled={isPending}
									className="flex w-full items-center justify-center gap-2 rounded-xl border border-stone-200 bg-stone-100 px-4 py-4 text-sm font-bold text-stone-900 shadow-sm transition-colors hover:border-stone-300 hover:bg-stone-50 disabled:opacity-50"
								>
									<Lock className="size-4 shrink-0 stroke-[2.5]" />
									{company?.password_configured
										? 'Alterar senha de acesso'
										: 'Definir senha de acesso'}
								</button>
							</>
						)}
					</div>

					<div className="flex shrink-0 items-center justify-end gap-6 border-t border-stone-50 p-8">
						<button
							type="button"
							onClick={
								isPasswordStep ? goBackToEditStep : handleClose
							}
							disabled={isPending}
							className="text-sm font-bold text-stone-400 hover:text-stone-600 disabled:opacity-50"
						>
							{isPasswordStep ? 'Voltar' : 'Cancelar'}
						</button>
						<Button
							type="submit"
							text={
								isPasswordStep
									? isSavingPassword
										? 'Salvando...'
										: 'Salvar senha'
									: isPending
									  ? 'Salvando...'
									  : 'Salvar'
							}
							disabled={isPending}
							className="min-w-[160px] rounded-full bg-black px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800"
							startIcon={
								isPending ? (
									<Loader2 className="size-4 animate-spin" />
								) : isPasswordStep ? (
									<Lock className="size-4 stroke-[3]" />
								) : (
									<Check className="size-4 stroke-[3]" />
								)
							}
						/>
					</div>
				</form>
			</div>
		</div>,
		document.body,
	);
};
